import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Trophy, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Page } from '@/components/layout/Page';
import { PageContent } from '@/components/layout/PageContent';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { OpenBetCard } from '@/components/bets/OpenBetCard';
import { ScoredBetCard } from '@/components/bets/ScoredBetCard';
import { MY_BETS } from '@/graphql/queries';
import { BetSortField, RaceStatus, SortOrder, type MyBetsQuery } from '@/gql/graphql';
import { Skeleton } from '@/components/ui/Skeleton';

type BetItem = NonNullable<NonNullable<MyBetsQuery['myBets']>['items']>[number];
type Tab = 'open' | 'scored';
type ScoredSort = 'latest' | 'bestScore';

const PAGE_SIZE = 20;

function sortBets(bets: BetItem[], sort: ScoredSort): BetItem[] {
    if (sort !== 'bestScore') return bets;
    return [...bets].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
}

function BetCardSkeleton() {
    return (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                    <Skeleton className="h-5 w-44" />
                    <Skeleton className="h-4 w-16 rounded-full" />
                </div>
                <Skeleton className="w-14 h-8 rounded-lg" />
            </div>
            <div className="space-y-3">
                <Skeleton className="h-4 w-16 rounded-full" />
                <div className="space-y-2">
                    <Skeleton className="h-5 w-full rounded-lg" />
                    <Skeleton className="h-5 w-full rounded-lg" />
                    <Skeleton className="h-5 w-3/4 rounded-lg" />
                </div>
                <Skeleton className="h-4 w-16 rounded-full" />
                <div className="space-y-2">
                    <Skeleton className="h-5 w-full rounded-lg" />
                    <Skeleton className="h-5 w-full rounded-lg" />
                </div>
            </div>
            <Skeleton className="h-px w-full" />
            <div className="flex justify-between">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-24" />
            </div>
        </div>
    );
}

function BetListSkeleton() {
    return (
        <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
                <BetCardSkeleton key={i} />
            ))}
        </div>
    );
}

function getSortVariables(tab: Tab, scoredSort: ScoredSort) {
    if (tab === 'open') {
        return { field: BetSortField.UpdatedAt, order: SortOrder.Desc };
    }
    if (scoredSort === 'bestScore') {
        return { field: BetSortField.Score, order: SortOrder.Desc };
    }
    return { field: BetSortField.UpdatedAt, order: SortOrder.Desc };
}

export function MyBetsPage() {
    const { t } = useTranslation('bets');
    const { t: tc } = useTranslation();
    const [activeTab, setActiveTab] = useState<Tab>('open');
    const [scoredSort, setScoredSort] = useState<ScoredSort>('latest');
    const [hasAutoSwitched, setHasAutoSwitched] = useState(false);

    const { data, loading, error, fetchMore } = useQuery(MY_BETS, {
        variables: {
            pagination: { page: 1, limit: PAGE_SIZE },
            filter: { raceStatus: activeTab === 'open' ? RaceStatus.Open : RaceStatus.Ended },
            sort: getSortVariables(activeTab, scoredSort),
        },
        notifyOnNetworkStatusChange: true,
    });

    if (!hasAutoSwitched && activeTab === 'open' && data && !loading) {
        const items = data.myBets?.items || [];
        if (items.length === 0) {
            setActiveTab('scored');
        }
        setHasAutoSwitched(true);
    }

    const handleLoadMore = async () => {
        const currentPage = data?.myBets?.page || 1;
        await fetchMore({
            variables: {
                pagination: { page: currentPage + 1, limit: PAGE_SIZE },
                filter: { raceStatus: activeTab === 'open' ? RaceStatus.Open : RaceStatus.Ended },
                sort: getSortVariables(activeTab, scoredSort),
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;
                return {
                    myBets: {
                        ...fetchMoreResult.myBets,
                        items: [
                            ...(prev.myBets?.items || []),
                            ...(fetchMoreResult.myBets?.items || []),
                        ],
                    },
                };
            },
        });
    };

    const items = data?.myBets?.items;
    const hasNextPage = data?.myBets?.hasNextPage;
    const isInitialLoad = loading && !data;

    const sortedBets = useMemo(() => {
        const bets = items || [];
        return activeTab === 'scored' ? sortBets(bets, scoredSort) : bets;
    }, [items, activeTab, scoredSort]);

    return (
        <Page className="bg-slate-50">
            <PageContent width="standard" className="space-y-6 pb-24 md:pb-6">
                {/* Header with tabs and sort */}
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <h1 className="text-2xl font-black text-slate-900">{t('list.title')}</h1>

                    <div className="flex items-center gap-3">
                        {/* Tabs */}
                        <div className="flex gap-1.5">
                            {(['open', 'scored'] as const).map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={cn(
                                        "px-3 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition-all",
                                        activeTab === tab
                                            ? "bg-emerald-600 text-white shadow-sm"
                                            : "bg-white text-slate-500 border border-slate-100 hover:bg-slate-50"
                                    )}
                                >
                                    {t(`list.tabs.${tab}`)}
                                </button>
                            ))}
                        </div>

                        {/* Sort (only for scored tab) */}
                        {activeTab === 'scored' && (
                            <div className="flex gap-1 border-l border-slate-200 pl-3">
                                {(['latest', 'bestScore'] as const).map((sort) => (
                                    <button
                                        key={sort}
                                        onClick={() => setScoredSort(sort)}
                                        className={cn(
                                            "px-2.5 py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold transition-all",
                                            scoredSort === sort
                                                ? "bg-slate-800 text-white"
                                                : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                                        )}
                                    >
                                        {t(`list.sort.${sort}`)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Loading */}
                {isInitialLoad && <BetListSkeleton />}

                {/* Error */}
                {error && <ErrorBanner message={tc('error.generic')} />}

                {/* Bets */}
                {!isInitialLoad && sortedBets.length > 0 && (
                    <div className="space-y-4">
                        {sortedBets.map(bet => (
                            activeTab === 'open'
                                ? <OpenBetCard key={bet.id} bet={bet} />
                                : <ScoredBetCard key={bet.id} bet={bet} />
                        ))}
                    </div>
                )}

                {/* Empty states */}
                {!loading && sortedBets.length === 0 && !error && (
                    <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-white rounded-2xl border border-dashed border-slate-200">
                        <Trophy size={40} className="mb-3 opacity-20" />
                        {activeTab === 'open' ? (
                            <>
                                <p className="text-sm font-medium mb-2">{t('list.emptyOpen')}</p>
                                <Link
                                    to="/races"
                                    className="text-emerald-600 font-bold text-sm hover:underline"
                                >
                                    {t('list.emptyOpenAction')}
                                </Link>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-medium mb-1">{t('list.emptyScored')}</p>
                                <p className="text-xs text-slate-400">{t('list.emptyScoredDesc')}</p>
                            </>
                        )}
                    </div>
                )}

                {/* Load more */}
                {hasNextPage && (
                    <div className="pt-2 flex justify-center">
                        <button
                            onClick={handleLoadMore}
                            disabled={loading}
                            className="bg-white border border-slate-200 text-slate-600 px-6 py-3 rounded-xl font-bold text-sm hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center gap-2 shadow-sm"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin" size={16} />
                                    {tc('btn.loading')}
                                </>
                            ) : (
                                t('list.loadMore')
                            )}
                        </button>
                    </div>
                )}
            </PageContent>
        </Page>
    );
}
